<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from 'lodash'
import { ref, onMounted, computed, watch, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'
import IconBase from '../icon/IconBase.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconCopy from '../icon/IconCopy.vue'
import IconExternalLink from '../icon/IconExternalLink.vue'
import IconBin from '../icon/IconBin.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormText from '../Atoms/FormText.vue'
import VodClipTrimmingModal from '@/components/modals/VodClipTrimmingModal.vue'
import StreamService from '@/services/StreamService'
import Title from '../ui/Title.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ButtonCopyToClipboard from '../Moleculas/ButtonCopyToClipboard.vue'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  playerUrl: {
    type: String
  }
})

const emit = defineEmits({
  'clips-update': () => true,
})

const clips = ref(null)
const selectedClipIndex = ref(null)

onMounted(async () => {
  getClips()
})

watch(() => `${route.params.streamId}/${route.params.videoId}`, async () => {
  getClips()
})

const getClips = async () => {
  const result = await StreamService.getVodClips(route.params.streamId, props.vodFile?._id)
  clips.value = result
}

const openClipTrimmingModal = async () => {
  setTimeout(() => {
    showModal('modal-vod-clip-trimming');
  }, 100);
}
const getVodClipIframeSnippet = (clip) => {
  const iframeSrc = props.playerUrl;
  return `<iframe src="${iframeSrc}${props.vodFile.archive ? '&' : '?'}clip=${clip.id}" width="590" height="431" frameborder="0" scrolling="no" allow="autoplay; encrypted-media" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`
}

const getVodClipDirectUrl = (clip) => {
  const iframeSrc = props.playerUrl;
  return `${iframeSrc}${props.vodFile.archive ? '&' : '?'}clip=${clip.id}`
}

const onClipNameChange = async (newName, clip) => {
  try {
    await StreamService.setVodClipName(route.params.streamId, clip.id, newName)
    notify({ group: 'success', text: 'Clip name was changed' });
  } catch (err) {
    notify({
      group: 'error',
      title: "Couldn't change clip name",
      text: err.message
    });
  }
}
const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value);
  notify({ group: 'info', text: 'Copied to clipboard' });
}
const trackClipEmbedCopied = (clip) => {
  track(EVENTS.EMBED_CODE_COPIED, {
    contentType: 'clip',
    copyType: 'embed_code',
    folderId: props.vodFolder?._id,
    fileId: props.vodFile?._id,
    clipId: clip?.id,
  })
}
const trackClipUrlCopied = (clip) => {
  track(EVENTS.CONTENT_URL_COPIED, {
    contentType: 'clip',
    copyType: 'direct',
    folderId: props.vodFolder?._id,
    fileId: props.vodFile?._id,
    clipId: clip?.id,
  })
}
const removeSelectedVodClip = (index) => {
  selectedClipIndex.value = index
  showModal('confirm-clip-removal');
  return
}
const removeVideoClip = async () => {
  const clip = clips.value[selectedClipIndex.value]
  if (!clip.id) {
    clips.value.splice(selectedClipIndex.value, 1);
    return
  }
  try {
    await StreamService.deleteVodClip(route.params.streamId, props.vodFile._id, clip.id)
    clips.value.splice(selectedClipIndex.value, 1)
    emit('clips-update', clip);
    notify({ group: 'success', text: 'Clip deleted' })
  } catch (err) {
    console.log(err)
  }
}
const humanTimeRange = (duration) => {
  return moment.utc(moment.duration(duration, "s").asMilliseconds()).format("HH:mm:ss")
}
const onVodClipRangeCreated = async (clip) => {
  clips.value.push(clip)
  notify({ group: 'success', text: 'Clip added' })
  emit('clips-update', clip);
}
</script>

<template>
  <section>
    <!-- Header -->
    <div class="flex items-start justify-between gap-3 mb-1">
      <h3 class="text-base font-medium text-white flex items-center gap-2">
        Clip video
        <span v-if="clips?.length"
          class="text-tiny font-medium text-surface-9 bg-surface-3 rounded px-1.5 py-0.5 leading-none">{{ clips.length }}</span>
      </h3>
      <FormButton type="main" size="sm" class="shrink-0" @click="openClipTrimmingModal()">
        <icon-base class="mr-1 shrink-0" fill="transparent">
          <icon-plus />
        </icon-base>
        Add new clip
      </FormButton>
    </div>
    <p class="text-surface-8 text-xs mb-4">Cut a clip from your videos and start sharing. If the source file is
      deleted, all the clips generated from it will also be removed.</p>

    <!-- Clip cards -->
    <div v-if="clips?.length > 0" class="flex flex-col gap-3">
      <div v-for="(clip, index) in clips" :key="clip.id" class="rounded-lg ring-1 ring-surface-3 bg-surface-3 p-3">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <Title :editable="true" @changed="value => onClipNameChange(value, clip)" :title="clip.name" />
            <span class="text-surface-8 text-tiny">{{ humanTimeRange((clip.endSec - clip.startSec) + 1) }}</span>
          </div>
          <FormButton @click="removeSelectedVodClip(index)" type="link" isIcon size="sm"
            customClasses="shrink-0 text-surface-8 hover:text-error">
            <icon-base class="shrink-0" fill="transparent">
              <icon-bin />
            </icon-base>
          </FormButton>
        </div>
        <FormText readonly custom-classes="!bg-surface-2 !h-16 text-xs resize-none cursor-pointer mt-2"
          @click="clipboardCopy(getVodClipIframeSnippet(clip)); trackClipEmbedCopied(clip)" :model-value="getVodClipIframeSnippet(clip)" />
        <div class="flex items-center gap-2 mt-2 flex-wrap">
          <ButtonCopyToClipboard :text="getVodClipIframeSnippet(clip)" withText label="Copy to Clipboard"
            fixedWidth="" @click="trackClipEmbedCopied(clip)" />
          <ButtonCopyToClipboard :text="getVodClipDirectUrl(clip)" withText label="Copy Direct URL" fixedWidth="" @click="trackClipUrlCopied(clip)" />
          <a :href="getVodClipDirectUrl(clip)" target="_blank"
            class="inline-flex items-center justify-center size-8 rounded-md ring-1 ring-surface-4 text-surface-text-1 hover:text-white hover:no-underline shrink-0">
            <icon-base class="shrink-0" fill="none">
              <icon-external-link />
            </icon-base>
          </a>
        </div>
      </div>
    </div>

    <confirm-modal :message="'You are about to delete clip'" modal-type="danger" modal-id="confirm-clip-removal"
      ok-text="Delete" cancel-text="Keep" @modal-confirm="removeVideoClip" />
    <vod-clip-trimming-modal v-if="props.vodFile?.srcHls" :hlsUrl="props.vodFile.srcHls" :video="props.vodFile"
      @request-vod-clip-range-created="onVodClipRangeCreated" />
  </section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
